import React from 'react';
import { BACKGROUND_TYPE, FestivalData, Homepage, ImageData, SHOW_ITEMS_LAYOUT } from '../../../../model/FestivalData';
import ImageEditor from '../ImageEditor/ImageEditor';
import ColorInput from './inputs/ColorInput';
import DateTimeInput from './inputs/DateTimeInput';
import NumberInput from './inputs/NumberInput';
import ThemeSection from './sections/ThemeSection';
import ThemeSectionGroup from './sections/ThemeSectionGroup';
import ImageEditorsGroup from './sections/ImageEditorsGroup';
import ShowListLayoutPicker from './showListLayout/ShowListLayoutPicker';
import PageHeader from '../../../../components/PageHeader';
import { useMessages } from '../../../../i18n/I18n';
import { contrastingTextColor, cssColor } from '../../../../utils/colors';
import './InfosEditor.css';

// Mirrors the app's built-in default and the API's accepted range
const SHOW_REMINDER_MINUTES_DEFAULT = 15;
const SHOW_REMINDER_MINUTES_MAX = 24 * 60;

const CORNER_RADIUS_MAX = 32;

// Their titles are in t.sections
const SECTIONS = [
  { id: 'infos-festival', key: 'festival' },
  { id: 'infos-theme', key: 'theme' },
  { id: 'infos-navigation', key: 'navigation' },
  { id: 'infos-surfaces', key: 'surfaces' },
  { id: 'infos-screens', key: 'screens' },
  { id: 'infos-images', key: 'images' },
] as const;

type Stylesheet = FestivalData['infos']['stylesheet'];
type ThemeSectionKey = 'surface' | 'top_bar' | 'segmented_control' | 'map';

// The API's defaults (ApiStylesheet.default_stylesheet) for the theme sections older data lacks
const THEME_SECTION_DEFAULTS: { [K in ThemeSectionKey]: NonNullable<Stylesheet[K]> } = {
  surface: { background_color: '#FFFFFF', title_color: '#000000', text_color: '#333333', sheet_background_color: '#F2F2F7' },
  top_bar: { background_color: '#FFFFFF', background_opacity: 0.5 },
  segmented_control: {
    text_color: '#000000',
    selected_text_color: '#000000',
    background_color: '#767680',
    background_opacity: 0.12,
    selected_background_color: '#FFFFFF',
  },
  map: { location_color: '#007AFF', pin_background_color: '#FFFFFF' },
};

const opacityOf = (e: React.ChangeEvent<HTMLInputElement>) => Math.min(1.0, Math.max(0.0, parseFloat(e.target.value) || 0));

interface InfosEditorProps {
  infos: FestivalData['infos'];
  onChange: (newInfos: FestivalData['infos']) => void;
  internalName: FestivalData['internal_name'];
  onInternalNameChange: (internalName: string | null) => void;
  getApiImageUrl: (identifier: string) => string;
}

const InfosEditor: React.FC<InfosEditorProps> = ({ infos, onChange, internalName, onInternalNameChange, getApiImageUrl }) => {
  const t = useMessages('infos');

  // Helper functions for state updates
  const setNestedValue = (obj: any, path: string[], value: any): any => {
    if (path.length === 0) return value;
    const [head, ...rest] = path;
    return { ...obj, [head]: setNestedValue(obj?.[head] ?? {}, rest, value) };
  };

  const createNumberChangeHandler = (
    path: string[],
    minValue: number = 0,
    maxValue: number = Infinity
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(maxValue, Math.max(minValue, parseInt(e.target.value) || 0));
    onChange(setNestedValue(infos, path, value));
  };

  const createColorChangeHandler = (
    path: string[]
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(setNestedValue(infos, path, e.target.value));
  };

  const updateStylesheet = (update: Partial<Stylesheet>) => {
    onChange({ ...infos, stylesheet: { ...infos.stylesheet, ...update } });
  };

  // A theme section is written whole, its other values as shown, so a section older data lacks is never saved in part
  const themeSection = <K extends ThemeSectionKey>(key: K): NonNullable<Stylesheet[K]> => (
    { ...THEME_SECTION_DEFAULTS[key], ...infos.stylesheet[key] }
  );
  const updateThemeSection = <K extends ThemeSectionKey>(key: K, update: Partial<NonNullable<Stylesheet[K]>>) => {
    updateStylesheet({ [key]: { ...themeSection(key), ...update } });
  };
  const surface = themeSection('surface');
  const topBar = themeSection('top_bar');
  const segmentedControl = themeSection('segmented_control');
  const map = themeSection('map');

  // Basic handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onChange({
      ...infos,
      [name]: value
    });
  };

  const handleLogoChange = (newImage: ImageData | undefined) => {
    onChange({
      ...infos,
      festival_logo: newImage || null
    });
  };

  const handleTicketImageChange = (newImage: ImageData | undefined) => {
    onChange({
      ...infos,
      tickets: {
        description: infos.tickets?.description || '',
        link: infos.tickets?.link || '',
        image: newImage || null
      }
    });
  };

  const handleNewsHeaderImageChange = (newImage: ImageData | undefined) => {
    onChange({
      ...infos,
      news_header_image: newImage || null
    });
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        [name]: value
      }
    });
  };

  // Until picked, the accent is automatic, the tab bar's selected color (and keeps following it), and the text on it
  // black or white, whichever reads best: null in the stylesheet, derived the same way by the apps
  const isAccentAutomatic = infos.stylesheet.accent_color == null;
  const isOnAccentAutomatic = infos.stylesheet.on_accent_color == null;
  const accentColor = infos.stylesheet.accent_color ?? infos.stylesheet.tab_bar?.selected_color ?? '#007AFF';
  const onAccentColor = infos.stylesheet.on_accent_color ?? contrastingTextColor(accentColor);
  const backToAutomatic = (key: 'accent_color' | 'on_accent_color') => (
    <button type="button" className="color-input-reset" onClick={() => updateStylesheet({ [key]: null })}>
      {t.brand.backToAutomatic}
    </button>
  );

  const handleBackgroundTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as BACKGROUND_TYPE;
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        background: {
          type: newType,
          primary_color: infos.stylesheet.background.primary_color || '#ffffff',
          // Kept for the other types too, so switching back finds them (the API drops them from the other types)
          secondary_color: newType === 'Gradient' ? (infos.stylesheet.background.secondary_color || '#ffffff') : infos.stylesheet.background.secondary_color,
          base_color: infos.stylesheet.background.base_color,
          image: newType === 'Image' ? infos.stylesheet.background.image : null,
          image_opacity: newType === 'Image' ? (infos.stylesheet.background.image_opacity ?? 1.0) : undefined,
        }
      }
    });
  };

  const handleBackgroundImageChange = (newImage: ImageData | undefined) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        background: {
          ...infos.stylesheet.background,
          image: newImage || null
        }
      }
    });
  };

  const handleBackgroundImageOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        background: {
          ...infos.stylesheet.background,
          image_opacity: opacityOf(e),
        }
      }
    });
  };

  const handleBackgroundBaseColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        background: {
          ...infos.stylesheet.background,
          base_color: e.target.value,
        }
      }
    });
  };

  // Use helper functions for all the repetitive handlers
  const handleShowCellHeightChange = createNumberChangeHandler(['stylesheet', 'show', 'cell_height'], 40);
  const handleNewsCellHeightChange = createNumberChangeHandler(['stylesheet', 'news', 'cell_height'], 60);
  const handleNewsPaddingChange = createNumberChangeHandler(['stylesheet', 'news', 'padding']);
  const handleNewsHeaderHeightChange = createNumberChangeHandler(['stylesheet', 'news', 'header_height']);
  const handleNewsItemsSpacingChange = createNumberChangeHandler(['stylesheet', 'news', 'items_spacing']);
  const handlePagesIconHeightChange = createNumberChangeHandler(['stylesheet', 'pages', 'icon_height']);
  const handlePagesHeaderHeightChange = createNumberChangeHandler(['stylesheet', 'pages', 'header_height']);
  const handleNotificationsPaddingChange = createNumberChangeHandler(['stylesheet', 'notifications', 'padding']);
  const handleNotificationsItemsSpacingChange = createNumberChangeHandler(['stylesheet', 'notifications', 'items_spacing']);
  const handleNotificationsVerticalSpacingChange = createNumberChangeHandler(['stylesheet', 'notifications', 'items_vertical_spacing']);
  const handleNavBarLogoHeightChange = createNumberChangeHandler(['stylesheet', 'nav_bar_logo_height']);
  const handleShowDetailsHeaderHeightChange = createNumberChangeHandler(['stylesheet', 'show', 'details_header_height']);
  const handleNewsDetailsHeaderHeightChange = createNumberChangeHandler(['stylesheet', 'news', 'details_header_height']);
  const handlePageRowHeightChange = createNumberChangeHandler(['stylesheet', 'pages', 'row_height']);
  const handlePageSectionHeightChange = createNumberChangeHandler(['stylesheet', 'pages', 'section_height']);
  const handleCornerRadiusChange = createNumberChangeHandler(['stylesheet', 'corner_radius'], 0, CORNER_RADIUS_MAX);

  const handleNewsItemsBackgroundColorChange = createColorChangeHandler(['stylesheet', 'news', 'items_background_color']);
  const handleNewsItemsTitleColorChange = createColorChangeHandler(['stylesheet', 'news', 'items_title_color']);
  const handleNewsItemsSubtitleColorChange = createColorChangeHandler(['stylesheet', 'news', 'items_subtitle_color']);
  const handleNewsItemsDateColorChange = createColorChangeHandler(['stylesheet', 'news', 'items_date_color']);
  const handleShowItemsArtistNameColorChange = createColorChangeHandler(['stylesheet', 'show', 'items_artist_name_color']);
  const handleShowItemsSceneNameColorChange = createColorChangeHandler(['stylesheet', 'show', 'items_scene_name_color']);
  const handleShowItemsShowDateColorChange = createColorChangeHandler(['stylesheet', 'show', 'items_show_date_color']);
  const handleShowTitleColorChange = createColorChangeHandler(['stylesheet', 'show', 'title_color']);
  const handleShowButtonsTintColorChange = createColorChangeHandler(['stylesheet', 'show', 'buttons_tint_color']);
  const handleShowItemsTagBackgroundColorChange = createColorChangeHandler(['stylesheet', 'show', 'items_tag_background_color']);
  const handleShowItemsTagTextColorChange = createColorChangeHandler(['stylesheet', 'show', 'items_tag_text_color']);
  const handleShowItemsLayoutChange = (layout: SHOW_ITEMS_LAYOUT) => {
    onChange(setNestedValue(infos, ['stylesheet', 'show', 'items_layout'], layout));
  };
  const showItemsLayout = infos.stylesheet.show.items_layout ?? 'card';
  const handlePageTitleColorChange = createColorChangeHandler(['stylesheet', 'pages', 'title_color']);
  const handlePageSectionColorChange = createColorChangeHandler(['stylesheet', 'pages', 'section_color']);
  const handlePagesIconTintColorChange = createColorChangeHandler(['stylesheet', 'pages', 'icon_tint_color']);
  const handleNewsTitleColorChange = createColorChangeHandler(['stylesheet', 'news', 'title_color']);
  const handleFestivalNameColorChange = createColorChangeHandler(['stylesheet', 'festival_name_color']);

  // The tab bar is written whole, its other values as shown
  const updateTabBar = (update: Partial<NonNullable<Stylesheet['tab_bar']>>) => {
    updateStylesheet({
      tab_bar: {
        selected_color: infos.stylesheet.tab_bar?.selected_color || '#007AFF',
        background_color: infos.stylesheet.tab_bar?.background_color || '#FFFFFF',
        background_opacity: infos.stylesheet.tab_bar?.background_opacity ?? 1.0,
        unselected_color: infos.stylesheet.tab_bar?.unselected_color ?? '#9CA3AF',
        ...update,
      }
    });
  };

  // Lineup release handlers
  const isLineupReleased = !infos.lineup_release_date || new Date(infos.lineup_release_date) <= new Date();
  const isPastDate = !!infos.lineup_release_date && new Date(infos.lineup_release_date) <= new Date();

  const handleLineupToggle = () => {
    if (isLineupReleased && !isPastDate) {
      // Currently available (no date set) -> hide lineup with a default future date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 7);
      tomorrow.setHours(12, 0, 0, 0);
      onChange({
        ...infos,
        lineup_release_date: tomorrow.toISOString().replace(/\.\d{3}Z$/, 'Z'),
      });
    } else {
      // Currently hidden or past date -> make available by clearing the date
      onChange({
        ...infos,
        lineup_release_date: null,
      });
    }
  };

  // An emptied field keeps the date: clearing it releases the lineup, which only the checkbox does
  const handleLineupReleaseDateChange = (date: string | null) => {
    if (date) {
      onChange({
        ...infos,
        lineup_release_date: date,
      });
    }
  };

  // Show reminder handler: empty input clears the override so the app default applies
  const handleShowReminderMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    onChange({
      ...infos,
      show_reminder_minutes: isNaN(value) ? null : Math.min(SHOW_REMINDER_MINUTES_MAX, Math.max(1, value)),
    });
  };

  const updateHomepage = (update: Partial<Homepage>) => {
    onChange({
      ...infos,
      homepage: {
        enabled: infos.homepage?.enabled ?? false,
        start_date: infos.homepage?.start_date ?? null,
        end_date: infos.homepage?.end_date ?? null,
        before_message: infos.homepage?.before_message ?? null,
        after_message: infos.homepage?.after_message ?? null,
        ...update,
      },
    });
  };

  // Empty Show List handlers
  const handleEmptyShowListBuyTicketButtonTextColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        empty_show_list: {
          buy_ticket_button_text_color: e.target.value,
          buy_ticket_button_background_color: infos.stylesheet.empty_show_list?.buy_ticket_button_background_color || '#007AFF',
          title_color: infos.stylesheet.empty_show_list?.title_color || '#000000',
        }
      }
    });
  };

  const handleEmptyShowListBuyTicketButtonBackgroundColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        empty_show_list: {
          buy_ticket_button_text_color: infos.stylesheet.empty_show_list?.buy_ticket_button_text_color || '#FFFFFF',
          buy_ticket_button_background_color: e.target.value,
          title_color: infos.stylesheet.empty_show_list?.title_color || '#000000',
        }
      }
    });
  };

  const handleEmptyShowListTitleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        empty_show_list: {
          buy_ticket_button_text_color: infos.stylesheet.empty_show_list?.buy_ticket_button_text_color || '#FFFFFF',
          buy_ticket_button_background_color: infos.stylesheet.empty_show_list?.buy_ticket_button_background_color || '#007AFF',
          title_color: e.target.value,
        }
      }
    });
  };

  // Tickets handlers
  const handleTicketsBuyTicketButtonTextColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        tickets: {
          buy_ticket_button_text_color: e.target.value,
          buy_ticket_button_background_color: infos.stylesheet.tickets?.buy_ticket_button_background_color || '#007AFF',
        }
      }
    });
  };

  const handleTicketsBuyTicketButtonBackgroundColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...infos,
      stylesheet: {
        ...infos.stylesheet,
        tickets: {
          buy_ticket_button_text_color: infos.stylesheet.tickets?.buy_ticket_button_text_color || '#FFFFFF',
          buy_ticket_button_background_color: e.target.value,
        }
      }
    });
  };

  return (
    <section className="infos-editor">
      <PageHeader title={t.title} description={t.description} />
      <nav className="infos-section-nav" aria-label={t.sectionsLabel}>
        {SECTIONS.map(section => (
          <button
            type="button"
            key={section.id}
            onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {t.sections[section.key]}
          </button>
        ))}
      </nav>

      <ThemeSection id="infos-festival" title={t.sections.festival}>
        <ThemeSectionGroup title="">
          <div className="form-group">
            <label htmlFor="festival_name">{t.festival.name}</label>
            <input
              id="festival_name"
              type="text"
              name="festival_name"
              value={infos.festival_name}
              onChange={handleChange}
            />
            <span className="field-description">{t.festival.nameHelp}</span>
          </div>
          <div className="form-group">
            <label htmlFor="internal_name">{t.festival.internalName}</label>
            <input
              id="internal_name"
              type="text"
              value={internalName ?? ''}
              placeholder={infos.festival_name}
              onChange={(e) => onInternalNameChange(e.target.value || null)}
            />
            <span className="field-description">{t.festival.internalNameHelp}</span>
          </div>
        </ThemeSectionGroup>
        <ThemeSectionGroup title={t.lineup.title}>
          <div className="lineup-toggle">
            <input
              id="lineup_available"
              type="checkbox"
              checked={isLineupReleased}
              onChange={handleLineupToggle}
            />
            <label htmlFor="lineup_available">{t.lineup.available}</label>
          </div>
          {infos.lineup_release_date && (
            <div className="lineup-release-date-input">
              <label htmlFor="lineup_release_date">{t.lineup.releaseDate}</label>
              <DateTimeInput
                id="lineup_release_date"
                value={infos.lineup_release_date}
                onChange={handleLineupReleaseDateChange}
                disabled={isPastDate}
              />
              {isPastDate && (
                <div className="lineup-release-note">
                  {t.lineup.releaseDatePast}
                </div>
              )}
            </div>
          )}
          <span className="field-description">
            {t.lineup.help}
          </span>
        </ThemeSectionGroup>
        <ThemeSectionGroup title={t.reminders.title}>
          <div className="form-group">
            <label htmlFor="show_reminder_minutes">{t.reminders.minutes}</label>
            <input
              id="show_reminder_minutes"
              type="number"
              name="show_reminder_minutes"
              value={infos.show_reminder_minutes ?? ''}
              placeholder={t.reminders.placeholder(SHOW_REMINDER_MINUTES_DEFAULT)}
              onChange={handleShowReminderMinutesChange}
              min={1}
              max={SHOW_REMINDER_MINUTES_MAX}
              step={1}
            />
            <span className="field-description">
              {t.reminders.help(SHOW_REMINDER_MINUTES_DEFAULT)}
            </span>
          </div>
        </ThemeSectionGroup>
      </ThemeSection>

      <ThemeSection id="infos-theme" title={t.sections.theme}>
        <ThemeSectionGroup title={t.brand.title}>
          <ColorInput
            id="accent_color"
            name="accent_color"
            value={accentColor}
            onChange={(e) => updateStylesheet({ accent_color: e.target.value })}
            label={t.brand.accentColor}
            description={isAccentAutomatic ? t.brand.accentColorAutomatic : t.brand.accentColorHelp}
            action={!isAccentAutomatic && backToAutomatic('accent_color')}
            placeholder="#007AFF"
          />
          <ColorInput
            id="on_accent_color"
            name="on_accent_color"
            value={onAccentColor}
            onChange={(e) => updateStylesheet({ on_accent_color: e.target.value })}
            label={t.brand.onAccentColor}
            description={isOnAccentAutomatic ? t.brand.onAccentColorAutomatic : t.brand.onAccentColorHelp}
            action={!isOnAccentAutomatic && backToAutomatic('on_accent_color')}
            placeholder="#FFFFFF"
          />
          <ColorInput
            id="link_color"
            name="link_color"
            value={infos.stylesheet.link_color ?? '#007AFF'}
            onChange={(e) => updateStylesheet({ link_color: e.target.value })}
            label={t.brand.linkColor}
            description={t.brand.linkColorHelp}
            placeholder="#007AFF"
          />
          <div
            className="brand-sample"
            aria-hidden="true"
            style={{ backgroundColor: cssColor(surface.background_color, '#FFFFFF'), borderRadius: infos.stylesheet.corner_radius ?? 8 }}
          >
            <span
              className="brand-sample-button"
              style={{ backgroundColor: cssColor(accentColor, '#007AFF'), color: cssColor(onAccentColor, '#FFFFFF') }}
            >
              {t.brand.sampleButton}
            </span>
            <span className="brand-sample-link" style={{ color: cssColor(infos.stylesheet.link_color, '#007AFF') }}>
              {t.brand.sampleLink}
            </span>
          </div>
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.text.title} description={t.text.help}>
          <ColorInput
            id="title_color"
            name="title_color"
            value={infos.stylesheet.title_color}
            onChange={handleThemeChange}
            label={t.text.titleColor}
          />
          <ColorInput
            id="body_color"
            name="body_color"
            value={infos.stylesheet.body_color}
            onChange={handleThemeChange}
            label={t.text.bodyColor}
            placeholder="#333333"
          />
          <ColorInput
            id="caption_color"
            name="caption_color"
            value={infos.stylesheet.caption_color}
            onChange={handleThemeChange}
            label={t.text.captionColor}
            placeholder="#666666"
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.background.title}>
          <div className="form-group">
            <label htmlFor="background_type">{t.background.type}</label>
            <select
              id="background_type"
              value={infos.stylesheet.background.type}
              onChange={handleBackgroundTypeChange}
            >
              <option value="Color">{t.background.types.color}</option>
              <option value="Gradient">{t.background.types.gradient}</option>
              <option value="Image">{t.background.types.image}</option>
            </select>
          </div>

          {(infos.stylesheet.background.type === 'Color' || infos.stylesheet.background.type === 'Gradient') && (
            <ColorInput
              id="primary_color"
              name="primary_color"
              value={infos.stylesheet.background.primary_color || ''}
              onChange={(e) => onChange({
                ...infos,
                stylesheet: {
                  ...infos.stylesheet,
                  background: {
                    ...infos.stylesheet.background,
                    primary_color: e.target.value,
                  }
                }
              })}
              label={t.background.primaryColor}
            />
          )}

          {infos.stylesheet.background.type === 'Gradient' && (
            <ColorInput
              id="secondary_color"
              name="secondary_color"
              value={infos.stylesheet.background.secondary_color || ''}
              onChange={(e) => onChange({
                ...infos,
                stylesheet: {
                  ...infos.stylesheet,
                  background: {
                    ...infos.stylesheet.background,
                    secondary_color: e.target.value,
                  }
                }
              })}
              label={t.background.secondaryColor}
            />
          )}

          {infos.stylesheet.background.type === 'Image' && (
            <>
              <div className="form-group">
                <label>{t.background.image}</label>
                <ImageEditor
                  currentImage={infos.stylesheet.background.image || undefined}
                  onChange={handleBackgroundImageChange}
                  getApiImageUrl={getApiImageUrl}
                  fillMode="cover"
                />
              </div>
              <NumberInput
                id="background_image_opacity"
                name="background_image_opacity"
                value={infos.stylesheet.background.image_opacity ?? 1.0}
                onChange={handleBackgroundImageOpacityChange}
                label={t.background.imageOpacity}
                description={t.opacityHelp}
                min={0}
                max={1}
                step={0.1}
              />
              <ColorInput
                id="background_base_color"
                name="background_base_color"
                value={infos.stylesheet.background.base_color ?? '#ffffff'}
                onChange={handleBackgroundBaseColorChange}
                label={t.background.baseColor}
                description={t.background.baseColorHelp}
                placeholder="#ffffff"
              />
            </>
          )}
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.shapes.title}>
          <NumberInput
            id="corner_radius"
            name="corner_radius"
            value={infos.stylesheet.corner_radius ?? 8}
            onChange={handleCornerRadiusChange}
            label={t.shapes.cornerRadius}
            description={t.shapes.cornerRadiusHelp}
            max={CORNER_RADIUS_MAX}
          />
        </ThemeSectionGroup>
      </ThemeSection>

      <ThemeSection id="infos-navigation" title={t.sections.navigation}>
        <ThemeSectionGroup title={t.topBar.title}>
          <NumberInput
            id="nav_bar_logo_height"
            name="nav_bar_logo_height"
            value={infos.stylesheet.nav_bar_logo_height}
            onChange={handleNavBarLogoHeightChange}
            label={t.topBar.logoHeight}
            description={t.topBar.logoHeightHelp}
          />
          <ColorInput
            id="festival_name_color"
            name="festival_name_color"
            value={infos.stylesheet.festival_name_color || '#000000'}
            onChange={handleFestivalNameColorChange}
            label={t.topBar.festivalNameColor}
            description={t.topBar.festivalNameColorHelp}
            placeholder="#000000"
          />
          <ColorInput
            id="top_bar_background_color"
            name="top_bar_background_color"
            value={topBar.background_color}
            onChange={(e) => updateThemeSection('top_bar', { background_color: e.target.value })}
            label={t.topBar.backgroundColor}
            description={t.topBar.backgroundColorHelp}
            placeholder="#FFFFFF"
          />
          <NumberInput
            id="top_bar_background_opacity"
            name="top_bar_background_opacity"
            value={topBar.background_opacity}
            onChange={(e) => updateThemeSection('top_bar', { background_opacity: opacityOf(e) })}
            label={t.topBar.backgroundOpacity}
            description={t.opacityHelp}
            min={0}
            max={1}
            step={0.1}
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.tabBar.title}>
          <ColorInput
            id="tab_bar_selected_color"
            name="tab_bar_selected_color"
            value={infos.stylesheet.tab_bar?.selected_color || '#007AFF'}
            onChange={(e) => updateTabBar({ selected_color: e.target.value })}
            label={t.tabBar.selectedColor}
            placeholder="#007AFF"
          />
          <ColorInput
            id="tab_bar_unselected_color"
            name="tab_bar_unselected_color"
            value={infos.stylesheet.tab_bar?.unselected_color ?? '#9CA3AF'}
            onChange={(e) => updateTabBar({ unselected_color: e.target.value })}
            label={t.tabBar.unselectedColor}
            description={t.tabBar.unselectedColorHelp}
            placeholder="#9CA3AF"
          />
          <ColorInput
            id="tab_bar_background_color"
            name="tab_bar_background_color"
            value={infos.stylesheet.tab_bar?.background_color || '#FFFFFF'}
            onChange={(e) => updateTabBar({ background_color: e.target.value })}
            label={t.tabBar.backgroundColor}
            placeholder="#FFFFFF"
          />
          <NumberInput
            id="tab_bar_background_opacity"
            name="tab_bar_background_opacity"
            value={infos.stylesheet.tab_bar?.background_opacity ?? 1.0}
            onChange={(e) => updateTabBar({ background_opacity: opacityOf(e) })}
            label={t.tabBar.backgroundOpacity}
            description={t.opacityHelp}
            min={0}
            max={1}
            step={0.1}
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.segmentedControl.title} description={t.segmentedControl.help}>
          <ColorInput
            id="segmented_control_text_color"
            name="segmented_control_text_color"
            value={segmentedControl.text_color}
            onChange={(e) => updateThemeSection('segmented_control', { text_color: e.target.value })}
            label={t.segmentedControl.textColor}
          />
          <ColorInput
            id="segmented_control_selected_text_color"
            name="segmented_control_selected_text_color"
            value={segmentedControl.selected_text_color}
            onChange={(e) => updateThemeSection('segmented_control', { selected_text_color: e.target.value })}
            label={t.segmentedControl.selectedTextColor}
          />
          <ColorInput
            id="segmented_control_background_color"
            name="segmented_control_background_color"
            value={segmentedControl.background_color}
            onChange={(e) => updateThemeSection('segmented_control', { background_color: e.target.value })}
            label={t.segmentedControl.backgroundColor}
            description={t.segmentedControl.backgroundColorHelp}
            placeholder="#767680"
          />
          <NumberInput
            id="segmented_control_background_opacity"
            name="segmented_control_background_opacity"
            value={segmentedControl.background_opacity}
            onChange={(e) => updateThemeSection('segmented_control', { background_opacity: opacityOf(e) })}
            label={t.segmentedControl.backgroundOpacity}
            description={t.opacityHelp}
            min={0}
            max={1}
            step={0.1}
          />
          <ColorInput
            id="segmented_control_selected_background_color"
            name="segmented_control_selected_background_color"
            value={segmentedControl.selected_background_color}
            onChange={(e) => updateThemeSection('segmented_control', { selected_background_color: e.target.value })}
            label={t.segmentedControl.selectedBackgroundColor}
            description={t.segmentedControl.selectedBackgroundColorHelp}
            placeholder="#FFFFFF"
          />
        </ThemeSectionGroup>
      </ThemeSection>

      <ThemeSection id="infos-surfaces" title={t.sections.surfaces}>
        <ThemeSectionGroup title={t.surface.title}>
          <ColorInput
            id="surface_background_color"
            name="surface_background_color"
            value={surface.background_color}
            onChange={(e) => updateThemeSection('surface', { background_color: e.target.value })}
            label={t.surface.backgroundColor}
            description={t.surface.backgroundColorHelp}
            placeholder="#FFFFFF"
          />
          <ColorInput
            id="surface_title_color"
            name="surface_title_color"
            value={surface.title_color}
            onChange={(e) => updateThemeSection('surface', { title_color: e.target.value })}
            label={t.surface.titleColor}
            description={t.surface.titleColorHelp}
          />
          <ColorInput
            id="surface_text_color"
            name="surface_text_color"
            value={surface.text_color}
            onChange={(e) => updateThemeSection('surface', { text_color: e.target.value })}
            label={t.surface.textColor}
            description={t.surface.textColorHelp}
            placeholder="#333333"
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.sheet.title}>
          <ColorInput
            id="surface_sheet_background_color"
            name="surface_sheet_background_color"
            value={surface.sheet_background_color}
            onChange={(e) => updateThemeSection('surface', { sheet_background_color: e.target.value })}
            label={t.sheet.backgroundColor}
            description={t.sheet.backgroundColorHelp}
            placeholder="#F2F2F7"
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.imageText.title}>
          <ColorInput
            id="image_overlay_color"
            name="image_overlay_color"
            value={infos.stylesheet.image_overlay_color ?? '#000000'}
            onChange={(e) => updateStylesheet({ image_overlay_color: e.target.value })}
            label={t.imageText.overlayColor}
            description={t.imageText.overlayColorHelp}
          />
          <NumberInput
            id="image_overlay_opacity"
            name="image_overlay_opacity"
            value={infos.stylesheet.image_overlay_opacity ?? 0.7}
            onChange={(e) => updateStylesheet({ image_overlay_opacity: opacityOf(e) })}
            label={t.imageText.overlayOpacity}
            description={t.imageText.overlayOpacityHelp}
            min={0}
            max={1}
            step={0.1}
          />
          <ColorInput
            id="image_text_color"
            name="image_text_color"
            value={infos.stylesheet.image_text_color ?? '#FFFFFF'}
            onChange={(e) => updateStylesheet({ image_text_color: e.target.value })}
            label={t.imageText.textColor}
            description={t.imageText.textColorHelp}
            placeholder="#FFFFFF"
          />
        </ThemeSectionGroup>
      </ThemeSection>

      <ThemeSection id="infos-screens" title={t.sections.screens}>
        <ThemeSectionGroup title={t.homepage.title} className="homepage-group">
          <div>
            <div className="lineup-toggle">
              <input
                id="homepage_enabled"
                type="checkbox"
                checked={infos.homepage?.enabled ?? false}
                onChange={e => updateHomepage({ enabled: e.target.checked })}
              />
              <label htmlFor="homepage_enabled">{t.homepage.enabled}</label>
            </div>
            <span className="field-description">{t.homepage.help}</span>
          </div>
          {infos.homepage?.enabled && (
            <>
              <div>
                <div className="form-group">
                  <label htmlFor="homepage_start_date">{t.homepage.startDate}</label>
                  <DateTimeInput
                    id="homepage_start_date"
                    value={infos.homepage.start_date}
                    onChange={start_date => updateHomepage({ start_date })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="homepage_end_date">{t.homepage.endDate}</label>
                  <DateTimeInput
                    id="homepage_end_date"
                    value={infos.homepage.end_date}
                    onChange={end_date => updateHomepage({ end_date })}
                  />
                  <span className="field-description">{t.homepage.datesHelp}</span>
                </div>
              </div>
              <div>
                <div className="form-group">
                  <label htmlFor="homepage_before_message">{t.homepage.beforeMessage}</label>
                  <textarea
                    id="homepage_before_message"
                    value={infos.homepage.before_message ?? ''}
                    maxLength={2000}
                    onChange={e => updateHomepage({ before_message: e.target.value || null })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="homepage_after_message">{t.homepage.afterMessage}</label>
                  <textarea
                    id="homepage_after_message"
                    value={infos.homepage.after_message ?? ''}
                    maxLength={2000}
                    onChange={e => updateHomepage({ after_message: e.target.value || null })}
                  />
                  <span className="field-description">{t.homepage.messagesHelp}</span>
                </div>
              </div>
            </>
          )}
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.shows.title} description={t.showList.help} className="show-list-layout-group">
          <ShowListLayoutPicker
            stylesheet={infos.stylesheet}
            value={showItemsLayout}
            onChange={handleShowItemsLayoutChange}
            getApiImageUrl={getApiImageUrl}
          />
          <div className="show-list-layout-fields">
            {showItemsLayout === 'card' && (
              <NumberInput
                id="show_cell_height"
                name="show_cell_height"
                value={infos.stylesheet.show.cell_height}
                onChange={handleShowCellHeightChange}
                min={40}
                label={t.shows.cardHeight}
                description={t.shows.cardHeightHelp}
              />
            )}
            {showItemsLayout === 'row' && (
              <>
                <ColorInput
                  id="show_items_tag_background_color"
                  name="show_items_tag_background_color"
                  value={infos.stylesheet.show.items_tag_background_color ?? '#FFFFFF'}
                  onChange={handleShowItemsTagBackgroundColorChange}
                  label={t.showList.tagBackgroundColor}
                  placeholder="#FFFFFF"
                />
                <ColorInput
                  id="show_items_tag_text_color"
                  name="show_items_tag_text_color"
                  value={infos.stylesheet.show.items_tag_text_color ?? '#000000'}
                  onChange={handleShowItemsTagTextColorChange}
                  label={t.showList.tagTextColor}
                  placeholder="#000000"
                />
              </>
            )}
            <ColorInput
              id="show_items_artist_name_color"
              name="show_items_artist_name_color"
              value={infos.stylesheet.show.items_artist_name_color}
              onChange={handleShowItemsArtistNameColorChange}
              label={t.shows.artistNameColor}
            />
            <ColorInput
              id="show_items_scene_name_color"
              name="show_items_scene_name_color"
              value={infos.stylesheet.show.items_scene_name_color}
              onChange={handleShowItemsSceneNameColorChange}
              label={t.shows.stageNameColor}
              placeholder="#666666"
            />
            <ColorInput
              id="show_items_show_date_color"
              name="show_items_show_date_color"
              value={infos.stylesheet.show.items_show_date_color}
              onChange={handleShowItemsShowDateColorChange}
              label={t.shows.dateColor}
              placeholder="#999999"
            />
            <ColorInput
              id="show_title_color"
              name="show_title_color"
              value={infos.stylesheet.show.title_color || '#000000'}
              onChange={handleShowTitleColorChange}
              label={t.shows.sectionTitleColor}
              placeholder="#000000"
            />
            <ColorInput
              id="show_buttons_tint_color"
              name="show_buttons_tint_color"
              value={infos.stylesheet.show.buttons_tint_color || '#000000'}
              onChange={handleShowButtonsTintColorChange}
              label={t.shows.buttonsTintColor}
              placeholder="#000000"
            />
            <NumberInput
              id="show_details_header_height"
              name="show_details_header_height"
              value={infos.stylesheet.show.details_header_height}
              onChange={handleShowDetailsHeaderHeightChange}
              label={t.shows.detailsHeaderHeight}
              description={t.shows.detailsHeaderHeightHelp}
            />
          </div>
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.emptyLineup.title} description={t.emptyLineup.help}>
          <ColorInput
            id="empty_show_list_title_color"
            name="empty_show_list_title_color"
            value={infos.stylesheet.empty_show_list?.title_color || '#000000'}
            onChange={handleEmptyShowListTitleColorChange}
            label={t.emptyLineup.titleColor}
            placeholder="#000000"
          />
          <ColorInput
            id="empty_show_list_buy_ticket_button_text_color"
            name="empty_show_list_buy_ticket_button_text_color"
            value={infos.stylesheet.empty_show_list?.buy_ticket_button_text_color || '#FFFFFF'}
            onChange={handleEmptyShowListBuyTicketButtonTextColorChange}
            label={t.emptyLineup.buttonTextColor}
            placeholder="#FFFFFF"
          />
          <ColorInput
            id="empty_show_list_buy_ticket_button_background_color"
            name="empty_show_list_buy_ticket_button_background_color"
            value={infos.stylesheet.empty_show_list?.buy_ticket_button_background_color || '#007AFF'}
            onChange={handleEmptyShowListBuyTicketButtonBackgroundColorChange}
            label={t.emptyLineup.buttonBackgroundColor}
            placeholder="#007AFF"
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.news.title}>
          <NumberInput
            id="news_header_height"
            name="news_header_height"
            value={infos.stylesheet.news.header_height}
            onChange={handleNewsHeaderHeightChange}
            label={t.news.headerHeight}
            description={t.news.headerHeightHelp}
          />
          <ColorInput
            id="news_title_color"
            name="news_title_color"
            value={infos.stylesheet.news.title_color}
            onChange={handleNewsTitleColorChange}
            label={t.news.titleColor}
          />
          <ColorInput
            id="news_items_background_color"
            name="news_items_background_color"
            value={infos.stylesheet.news.items_background_color}
            onChange={handleNewsItemsBackgroundColorChange}
            label={t.news.itemsBackgroundColor}
            placeholder="#ffffff"
          />
          <ColorInput
            id="news_items_title_color"
            name="news_items_title_color"
            value={infos.stylesheet.news.items_title_color}
            onChange={handleNewsItemsTitleColorChange}
            label={t.news.itemsTitleColor}
          />
          <ColorInput
            id="news_items_subtitle_color"
            name="news_items_subtitle_color"
            value={infos.stylesheet.news.items_subtitle_color}
            onChange={handleNewsItemsSubtitleColorChange}
            label={t.news.itemsSubtitleColor}
            placeholder="#666666"
          />
          <ColorInput
            id="news_items_date_color"
            name="news_items_date_color"
            value={infos.stylesheet.news.items_date_color}
            onChange={handleNewsItemsDateColorChange}
            label={t.news.itemsDateColor}
            placeholder="#999999"
          />
          <NumberInput
            id="news_item_cell_height"
            name="news_item_cell_height"
            value={infos.stylesheet.news.cell_height}
            onChange={handleNewsCellHeightChange}
            min={60}
            label={t.news.cellHeight}
            description={t.news.cellHeightHelp}
          />
          <NumberInput
            id="news_padding"
            name="news_padding"
            value={infos.stylesheet.news.padding}
            onChange={handleNewsPaddingChange}
            label={t.news.padding}
            description={t.news.paddingHelp}
          />
          <NumberInput
            id="items_spacing"
            name="items_spacing"
            value={infos.stylesheet.news.items_spacing}
            onChange={handleNewsItemsSpacingChange}
            label={t.news.itemsSpacing}
            description={t.news.itemsSpacingHelp}
          />
          <NumberInput
            id="news_details_header_height"
            name="news_details_header_height"
            value={infos.stylesheet.news.details_header_height}
            onChange={handleNewsDetailsHeaderHeightChange}
            label={t.news.detailsHeaderHeight}
            description={t.news.detailsHeaderHeightHelp}
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.pages.title}>
          <NumberInput
            id="page_section_height"
            name="page_section_height"
            value={infos.stylesheet.pages.section_height}
            onChange={handlePageSectionHeightChange}
            label={t.pages.sectionHeight}
            description={t.pages.sectionHeightHelp}
          />
          <NumberInput
            id="pages_icon_height"
            name="pages_icon_height"
            value={infos.stylesheet.pages.icon_height}
            onChange={handlePagesIconHeightChange}
            label={t.pages.iconHeight}
            description={t.pages.iconHeightHelp}
          />
          <NumberInput
            id="page_row_height"
            name="page_row_height"
            value={infos.stylesheet.pages.row_height}
            onChange={handlePageRowHeightChange}
            label={t.pages.rowHeight}
            description={t.pages.rowHeightHelp}
          />
          <ColorInput
            id="page_title_color"
            name="page_title_color"
            value={infos.stylesheet.pages.title_color}
            onChange={handlePageTitleColorChange}
            label={t.pages.titleColor}
          />
          <ColorInput
            id="page_section_color"
            name="page_section_color"
            value={infos.stylesheet.pages.section_color}
            onChange={handlePageSectionColorChange}
            label={t.pages.sectionColor}
            placeholder="#666666"
          />
          <ColorInput
            id="pages_icon_tint_color"
            name="pages_icon_tint_color"
            value={infos.stylesheet.pages.icon_tint_color || '#000000'}
            onChange={handlePagesIconTintColorChange}
            label={t.pages.iconTintColor}
            placeholder="#000000"
          />
          <NumberInput
            id="pages_header_height"
            name="pages_header_height"
            value={infos.stylesheet.pages.header_height}
            onChange={handlePagesHeaderHeightChange}
            label={t.pages.headerHeight}
            description={t.pages.headerHeightHelp}
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.notifications.title}>
          <NumberInput
            id="notifications_padding"
            name="notifications_padding"
            value={infos.stylesheet.notifications.padding}
            onChange={handleNotificationsPaddingChange}
            label={t.notifications.padding}
            description={t.notifications.paddingHelp}
          />
          <NumberInput
            id="notifications_items_spacing"
            name="notifications_items_spacing"
            value={infos.stylesheet.notifications.items_spacing}
            onChange={handleNotificationsItemsSpacingChange}
            label={t.notifications.itemsSpacing}
            description={t.notifications.itemsSpacingHelp}
          />
          <NumberInput
            id="notifications_vertical_spacing"
            name="notifications_vertical_spacing"
            value={infos.stylesheet.notifications.items_vertical_spacing}
            onChange={handleNotificationsVerticalSpacingChange}
            label={t.notifications.verticalSpacing}
            description={t.notifications.verticalSpacingHelp}
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.tickets.title}>
          <ColorInput
            id="tickets_buy_ticket_button_text_color"
            name="tickets_buy_ticket_button_text_color"
            value={infos.stylesheet.tickets?.buy_ticket_button_text_color || '#FFFFFF'}
            onChange={handleTicketsBuyTicketButtonTextColorChange}
            label={t.tickets.buttonTextColor}
            placeholder="#FFFFFF"
          />
          <ColorInput
            id="tickets_buy_ticket_button_background_color"
            name="tickets_buy_ticket_button_background_color"
            value={infos.stylesheet.tickets?.buy_ticket_button_background_color || '#007AFF'}
            onChange={handleTicketsBuyTicketButtonBackgroundColorChange}
            label={t.tickets.buttonBackgroundColor}
            placeholder="#007AFF"
          />
        </ThemeSectionGroup>

        <ThemeSectionGroup title={t.map.title}>
          <ColorInput
            id="map_location_color"
            name="map_location_color"
            value={map.location_color}
            onChange={(e) => updateThemeSection('map', { location_color: e.target.value })}
            label={t.map.locationColor}
            description={t.map.locationColorHelp}
            placeholder="#007AFF"
          />
          <ColorInput
            id="map_pin_background_color"
            name="map_pin_background_color"
            value={map.pin_background_color}
            onChange={(e) => updateThemeSection('map', { pin_background_color: e.target.value })}
            label={t.map.pinBackgroundColor}
            description={t.map.pinBackgroundColorHelp}
            placeholder="#FFFFFF"
          />
        </ThemeSectionGroup>
      </ThemeSection>

      <ThemeSection id="infos-images" title={t.sections.images}>
        <ImageEditorsGroup>
          <ImageEditor
            currentImage={infos.festival_logo || undefined}
            onChange={handleLogoChange}
            getApiImageUrl={getApiImageUrl}
            fillMode="contain"
            title={t.images.logo}
            caption={t.images.logoCaption}
          />
          <ImageEditor
            currentImage={infos.news_header_image || undefined}
            onChange={handleNewsHeaderImageChange}
            getApiImageUrl={getApiImageUrl}
            fillMode="cover"
            title={t.images.news}
            caption={t.images.newsCaption}
          />
          <ImageEditor
            currentImage={infos.tickets?.image || undefined}
            onChange={handleTicketImageChange}
            getApiImageUrl={getApiImageUrl}
            fillMode="cover"
            title={t.images.tickets}
            caption={t.images.ticketsCaption}
          />
        </ImageEditorsGroup>
      </ThemeSection>
    </section>
  );
};

export default InfosEditor;
